/**
 * Shared constants for the Document Hub upload flow.
 */
import { EXTENSION_TO_CONTENT_TYPE } from '../../constants/file-upload.constants';
import { ClientDocumentStatus, DocType, DocumentStatus } from './documentHub.schema';

/** Display labels for the document-type enum (mirrors the Figma "Doc Type" column). */
export const DOC_TYPE_LABELS: Record<DocType, string> = {
  [DocType.Financials]: 'Financials',
  [DocType.CapTable]: 'Cap Table',
  [DocType.DealDocs]: 'Deal Docs',
  [DocType.BoardDeck]: 'Board Deck',
  [DocType.EmailMemo]: 'Email Memo',
  [DocType.Other]: 'Other',
  [DocType.Unknown]: 'Unknown',
};

/**
 * Display labels for the internal lifecycle status. Not the "Status" column, which shows the
 * uploader-facing projection; kept for the places that reason about the raw lifecycle.
 */
export const DOCUMENT_STATUS_LABELS: Record<DocumentStatus, string> = {
  [DocumentStatus.Uploaded]: 'Uploaded',
  [DocumentStatus.Detecting]: 'Processing',
  [DocumentStatus.Detected]: 'Needs review',
  [DocumentStatus.Confirmed]: 'Confirmed',
  [DocumentStatus.Failed]: 'Failed',
};

/**
 * Display labels for the "Status" column — what the uploader sees. A failed run reads as
 * Analyzing on purpose: the reason is CS-only, and the uploader is told to wait rather than
 * shown an internal error.
 */
export const CLIENT_DOCUMENT_STATUS_LABELS: Record<ClientDocumentStatus, string> = {
  [ClientDocumentStatus.Analyzing]: 'Analyzing',
  [ClientDocumentStatus.NeedsReview]: 'Needs review',
  [ClientDocumentStatus.Extracting]: 'Extracting',
  [ClientDocumentStatus.Pending]: 'Identifying Set',
  [ClientDocumentStatus.PendingItem]: 'Incomplete Set',
  [ClientDocumentStatus.Complete]: 'Complete',
};

/**
 * Columns the documents grid can be grouped by, in header order. These ids are
 * persisted per user, so they are declared here rather than derived from the
 * column defs — renaming one is a migration, not a refactor.
 */
export const GROUPABLE_COLUMN_IDS = ['status', 'company', 'period', 'docType', 'round', 'package'] as const;

/**
 * Doc Type options for the Review modal dropdown (MuiSingleSelect Option shape).
 * `Unknown` is intentionally excluded — it is the undetected placeholder state,
 * not a user-selectable type, so it must never appear in the dropdown.
 */
export const DOC_TYPE_OPTIONS: { id: DocType; value: string }[] = (Object.keys(DOC_TYPE_LABELS) as DocType[])
  .filter((docType) => docType !== DocType.Unknown)
  .map((docType) => ({ id: docType, value: DOC_TYPE_LABELS[docType] }));

/**
 * Deal-doc taxonomy — id → label. Mirrors EVENT_TYPES / DOCUMENT_TYPES in Starbreaker's
 * services/sync/src/document-classification/taxonomy.ts (source of truth; the classifier emits
 * these ids and the Maggie API validates against the same set). Keep the three in lockstep.
 */
export const EVENT_TYPE_LABELS: Record<string, string> = {
  round_priced: 'Round (Priced)',
  note_convertible: 'Note (Convertible)',
  safe: 'SAFE',
  note_safe_conversion: 'Note/SAFE Conversion',
  m_and_a: 'M&A',
  ipo: 'IPO',
  secondary: 'Secondary',
  recap_restructure: 'Recap / Restructure',
  dividend_distribution: 'Dividend / Distribution',
  write_off_dissolution: 'Write-off / Dissolution',
  earnout_escrow_release: 'Earnout / Escrow Release',
};

export const DEAL_DOCUMENT_TYPE_LABELS: Record<string, string> = {
  spa: 'SPA',
  subscription_agmt: 'Subscription Agmt',
  closing_schedule_funds_flow: 'Closing Schedule / Funds Flow',
  cap_table: 'Cap Table',
  a_r_charter: 'A&R Charter',
  stockholder_consent: 'Stockholder Consent',
  ira_voting_rofr: 'IRA / Voting / ROFR',
  convertible_note: 'Convertible Note',
  note_purchase_agmt: 'Note Purchase Agmt',
  safe_agmt: 'SAFE Agmt',
  conversion_notice_schedule: 'Conversion Notice / Schedule',
  merger_agmt: 'Merger Agmt',
  apa: 'APA',
  letter_of_transmittal: 'Letter of Transmittal',
  payment_spreadsheet: 'Payment Spreadsheet',
  escrow_agmt: 'Escrow Agmt',
  earnout_agmt: 'Earnout Agmt',
  joinder_agmt: 'Joinder Agmt',
  form_8937: 'Form 8937',
  secondary_pa: 'Secondary PA',
  stock_power: 'Stock Power',
  restructure_recap_agmt: 'Restructure / Recap Agmt',
  dividend_notice: 'Dividend Notice',
  distribution_notice: 'Distribution Notice',
  dissolution_liquidation: 'Dissolution / Liquidation',
  final_k1_1099: 'Final K-1 / 1099',
  escrow_release_notice: 'Escrow Release Notice',
  earnout_achievement_notice: 'Earnout Achievement Notice',
  s1_prospectus: 'S-1 / Prospectus',
  underwriting_lockup: 'Underwriting / Lock-up',
  side_letter: 'Side Letter',
};

const toOptions = (labels: Record<string, string>): { id: string; value: string }[] =>
  Object.entries(labels).map(([id, value]) => ({ id, value }));

/** Dropdown options (MuiSingleSelect Option shape) — Deal Docs only. */
export const EVENT_TYPE_OPTIONS = toOptions(EVENT_TYPE_LABELS);
export const DEAL_DOCUMENT_TYPE_OPTIONS = toOptions(DEAL_DOCUMENT_TYPE_LABELS);

/**
 * Anchor document type(s) per event type — mirrors MANDATORY_DOCUMENTS in Starbreaker's
 * services/sync/src/deal-doc-event/mandatory-documents.ts (source of truth; keep in
 * lockstep, like the taxonomy labels above). A package is complete once ANY ONE of the
 * listed anchors is present (an OR-group); every event has exactly one anchor except
 * `earnout_escrow_release`, which has two.
 */
export const MANDATORY_DEAL_DOCUMENTS: Record<string, readonly string[]> = {
  round_priced: ['spa'],
  note_convertible: ['convertible_note'],
  safe: ['safe_agmt'],
  note_safe_conversion: ['conversion_notice_schedule'],
  acquisition_cash: ['merger_agmt'],
  acquisition_stock_rollover: ['merger_agmt'],
  acquisition_mixed: ['merger_agmt'],
  ipo: ['s1_prospectus'],
  secondary: ['secondary_pa'],
  recap_restructure: ['restructure_recap_agmt'],
  dividend_distribution: ['dividend_notice'],
  write_off_dissolution: ['dissolution_liquidation'],
  earnout_escrow_release: ['escrow_release_notice', 'earnout_achievement_notice'],
};

/** Label for a stored taxonomy id; falls back to the raw id so an unknown value is still visible. */
export const eventTypeLabel = (id: string | null | undefined): string | null =>
  id ? (EVENT_TYPE_LABELS[id] ?? id) : null;
export const dealDocumentTypeLabel = (id: string | null | undefined): string | null =>
  id ? (DEAL_DOCUMENT_TYPE_LABELS[id] ?? id) : null;

/**
 * Extension → content-type for Document Hub uploads. Spreads the shared upload
 * map (the source of truth backing `ALLOWED_EXTENSIONS` used by KPI/deal-docs/
 * company-docs flows) and supplements the two Document-Hub-only extensions the
 * shared map doesn't cover: legacy `.doc` and ZIP bundles. Extending the shared
 * map instead would silently start accepting `.doc`/`.zip` app-wide.
 */
export const CONTENT_TYPE_BY_EXTENSION: Record<string, string> = {
  ...EXTENSION_TO_CONTENT_TYPE,
  doc: 'application/msword',
  zip: 'application/zip',
};

/**
 * Accepted upload extensions. Derived from CONTENT_TYPE_BY_EXTENSION so the
 * accepted set and the content-type mapping can never drift apart.
 */
export const ACCEPTED_EXTENSIONS = Object.keys(CONTENT_TYPE_BY_EXTENSION);

/**
 * `accept` attribute for the hidden file <input>. Kept in sync with
 * ACCEPTED_EXTENSIONS; used only as a first-pass filter — real validation runs
 * in validateFiles() so drag-drop is covered too.
 */
export const ACCEPT_ATTRIBUTE = ACCEPTED_EXTENSIONS.map((ext) => `.${ext}`).join(',');

/**
 * Max upload size. The Figma dropzone helper reads "…up to 100MB"; that copy
 * governs. (The ZIP mock shows a multi-GB file — flagged as a design
 * discrepancy; revisit if product wants a larger ZIP ceiling.)
 */
export const MAX_FILE_SIZE_BYTES = 100 * 1024 * 1024;

/**
 * Helper copy under the dropzone primary line. Base wording is verbatim from
 * Figma (node 4191:11368); the macro-enabled/template mention was added
 * without enumerating all 6 extensions to keep the line short.
 */
export const DROPZONE_HELPER_TEXT =
  'XLSX, XLS, CSV, PDF, DOCS, PPTX formats (including macro-enabled/template variants), up to 100MB';
